import { useMemo, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { Banner } from '@/ui/components/bits';
import { categoryStats } from '@/domain/weakspots';
import {
  byRisk,
  countdownLabel,
  daysUntil,
  MIDTERM_CHAPTERS,
  MIDTERM_OBJECTIVES,
  MIDTERM_PARTS,
  MIDTERM_TOTAL,
  mockShape,
  readiness,
  startMidtermMock,
  type MidtermPart,
  type PartReadiness,
} from '@/domain/midterm';

const MOCK_QUESTIONS = 25;
const MOCK_MINUTES = 60;

/**
 * The mid-term, as a revision plan.
 *
 * The paper is fourteen parts with known point values, so this screen is
 * built around them rather than around topics: what each part asks, how many
 * questions the bank has for it, how he has been doing on it, and the one
 * button that practises exactly that.
 *
 * Sorting by marks at risk rather than by paper order is the whole idea. Gas
 * stoichiometry is eight marks and percent composition is five; an evening is
 * better spent on whichever of them he is actually dropping.
 */
export function MidtermScreen() {
  const { items, sessions, attempts, rollups } = useAppState();
  const [order, setOrder] = useState<'risk' | 'paper'>('risk');
  const [starting, setStarting] = useState(false);
  const [shortfall, setShortfall] = useState<MidtermPart[] | null>(null);

  const stats = useMemo(() => categoryStats(attempts, rollups), [attempts, rollups]);
  const rows = useMemo(() => readiness(items, sessions, stats), [items, sessions, stats]);
  const shown = order === 'risk' ? byRisk(rows) : rows;

  /** The two or three parts a mock paper leans on hardest, named. */
  const heaviest = useMemo(() => {
    const shape = mockShape(MOCK_QUESTIONS);
    return MIDTERM_PARTS.slice()
      .sort((a, b) => (shape.get(b.n) ?? 0) - (shape.get(a.n) ?? 0) || a.n - b.n)
      .slice(0, 3)
      .map((p) => `${shape.get(p.n)} on ${p.title.toLowerCase()}`)
      .join(', ');
  }, []);

  const days = daysUntil();
  const answered = rows.reduce((sum, r) => sum + r.attempts, 0);
  const covered = rows.filter((r) => r.available > 0).length;

  async function startMock() {
    setStarting(true);
    try {
      const { short } = await startMidtermMock({
        items,
        sessions,
        questions: MOCK_QUESTIONS,
        minutes: MOCK_MINUTES,
      });
      if (short.length > 0) setShortfall(short);
      navigate('/exam');
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1>Mid-term</h1>
        <button className="btn btn--sm print-hide" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="card stack">
        <div className="spread">
          <strong>Monday 9 November</strong>
          <span className={days <= 3 && days >= 0 ? 'chip chip--count midterm__soon' : 'chip chip--count'}>
            {countdownLabel()}
          </span>
        </div>
        <p className="faint" style={{ margin: 0 }}>
          Chapters {MIDTERM_CHAPTERS[0]}–{MIDTERM_CHAPTERS[MIDTERM_CHAPTERS.length - 1]},
          fourteen parts, {MIDTERM_TOTAL} marks. {covered} of {MIDTERM_PARTS.length} parts have
          questions in your bank; you have answered {answered} of them so far.
        </p>
      </div>

      <section className="stack">
        <div className="spread">
          <h2 className="section-title">The paper</h2>
          <div className="seg print-hide" role="tablist">
            <button
              className="seg__btn"
              role="tab"
              aria-pressed={order === 'risk'}
              onClick={() => setOrder('risk')}
            >
              Weakest first
            </button>
            <button
              className="seg__btn"
              role="tab"
              aria-pressed={order === 'paper'}
              onClick={() => setOrder('paper')}
            >
              Paper order
            </button>
          </div>
        </div>

        {order === 'risk' ? (
          <p className="faint" style={{ margin: 0 }}>
            Ordered by marks at risk — the part&rsquo;s point value times how much of it you are
            getting wrong. A part you have never attempted counts as fully at risk.
          </p>
        ) : null}

        <ol className="midterm__parts">
          {shown.map((row) => (
            <PartRow key={row.part.n} row={row} />
          ))}
        </ol>
      </section>

      <section className="stack">
        <h2 className="section-title">A paper to sit</h2>
        <p className="faint" style={{ margin: 0 }}>
          {MOCK_QUESTIONS} questions in {MOCK_MINUTES} minutes, drawn in the paper&rsquo;s own
          proportions, so the heaviest parts come up most: {heaviest}. Every part gets at least
          one question, and nothing is padded out — if the bank is short on a part, the paper is
          shorter.
        </p>
        <button className="btn btn--primary" disabled={starting} onClick={() => void startMock()}>
          {starting ? 'Building…' : 'Start a mock paper'}
        </button>
        {shortfall ? (
          <Banner kind="warn">
            Short on questions for {shortfall.map((p) => `part ${p.n}`).join(', ')}. The paper is
            smaller rather than padded out with something else.
          </Banner>
        ) : null}
      </section>

      <section className="stack">
        <h2 className="section-title">The learning objectives</h2>
        <p className="faint" style={{ margin: 0 }}>
          The seven things the paper is testing, and where each one lands on it.
        </p>
        <ul className="midterm__objectives">
          {MIDTERM_OBJECTIVES.map((o) => (
            <li key={o.title}>
              <strong>{o.title}</strong> — {o.detail}{' '}
              <span className="faint">
                Part{o.parts.length > 1 ? 's' : ''} {o.parts.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function PartRow({ row }: { row: PartReadiness }) {
  const { part } = row;
  const drill = part.categories.join(',');

  return (
    <li className="card stack midterm__part">
      <div className="spread">
        <strong>
          {part.n}. {part.title}
        </strong>
        <span className="chip chip--count">{part.points} marks</span>
      </div>

      <p style={{ margin: 0 }}>{part.asks}</p>

      <p className="faint" style={{ margin: 0 }}>
        {row.available > 0 ? `${row.available} questions in your bank` : 'Nothing in your bank yet'}
        {row.attempts > 0
          ? ` · ${Math.round((row.accuracy ?? 0) * 100)}% right over ${row.attempts} answers${
              row.thin ? ', too few to read much into' : ''
            }`
          : ' · not attempted'}
      </p>

      <div className="row print-hide">
        {row.available > 0 ? (
          <button
            className="btn btn--sm"
            onClick={() =>
              navigate('/quiz', {
                mode: 'cumulative',
                categories: drill,
                title: `Part ${part.n} · ${part.title}`,
                textbook: '1',
                t: String(Date.now()),
              })
            }
          >
            Quiz me
          </button>
        ) : null}
        <button
          className="btn btn--sm"
          onClick={() =>
            navigate('/study', {
              mode: 'cumulative',
              categories: drill,
              title: `Part ${part.n} · ${part.title}`,
              textbook: '1',
              t: String(Date.now()),
            })
          }
        >
          Cards
        </button>
        {part.generators?.length ? (
          <button
            className="btn btn--sm"
            onClick={() =>
              navigate('/problems', {
                gen: part.generators!.join(','),
                title: `Part ${part.n} · ${part.title}`,
              })
            }
          >
            Practice problems
          </button>
        ) : null}
        {part.tool ? (
          <button className="btn btn--sm" onClick={() => navigate(part.tool!.path, part.tool!.params)}>
            {part.tool.label}
          </button>
        ) : null}
      </div>
    </li>
  );
}
